import {
  takesNoValue,
  takesValueList,
} from "encharge-domain/lib/helpers/salesforceSyncCriteria";
import {
  DraftRule,
  FieldContext,
  FieldInfo,
  newRule,
  RuleFields,
} from "components/ConfigureStep/Authenticate/Salesforce/panel/salesforceCriteria.model";
import { FieldOption } from "components/ConfigureStep/Authenticate/Salesforce/panel/salesforceCriteria.fields";
import { SalesforceOpportunityFieldInfo } from "store/persistence/persistSalesforceTriggers";

/**
 * The Filters of the Opportunity trigger, as the screen edits them: a list of
 * rules that must all be true (plan 2.5.3). Pure functions with no React in
 * them.
 *
 * The step stores Filters as `{ rules }`, the rule groups of the sync criteria
 * (groups joined by OR, the rules of a group joined by AND, one Keep out list).
 * The screen edits the common case, ONE group of rules joined by AND. Filters
 * made some other way (the API, an AI agent) can have more groups or a Keep out
 * list; the screen shows that they exist and how big they are, keeps them as
 * they are until the marketer takes them off, and never rewrites them into a
 * list it could not show.
 */

/** The most rules the Filters take (the server's limit) */
export const FILTER_RULE_LIMIT = 20;

type StoredRule = {
  propertyName?: unknown;
  condition?: unknown;
  propertyValue?: unknown;
  propertyValues?: unknown;
};

const isRecord = (value: unknown): value is Record<string, any> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** The groups and the Keep out list inside stored Filters; none when they are not in the expected shape. */
const partsOf = (
  filters: { rules: unknown } | undefined
): { groups: StoredRule[][]; keepOut: StoredRule[] } | undefined => {
  const rules = filters?.rules;
  if (!isRecord(rules)) return undefined;
  const include = rules.include;
  const exclude = rules.exclude;
  const groups: StoredRule[][] = [];
  if (isRecord(include) && Array.isArray(include.conditions)) {
    include.conditions.forEach((group: unknown) => {
      if (isRecord(group) && Array.isArray(group.conditions)) {
        groups.push(group.conditions as StoredRule[]);
      }
    });
  }
  return {
    groups,
    keepOut:
      isRecord(exclude) && Array.isArray(exclude.conditions)
        ? (exclude.conditions as StoredRule[])
        : [],
  };
};

export type FiltersShape =
  | { kind: "none" }
  | { kind: "simple"; rules: StoredRule[] }
  /** more than one group, or a Keep out list: kept as they are */
  | { kind: "complex"; groups: number; rules: number; keepOut: number };

/** What the stored Filters hold, for the screen to know whether it can edit them. */
export const shapeOf = (
  filters: { rules: unknown } | undefined
): FiltersShape => {
  const parts = partsOf(filters);
  if (!parts)
    return filters
      ? { kind: "complex", groups: 0, rules: 0, keepOut: 0 }
      : { kind: "none" };
  const total = parts.groups.reduce((n, group) => n + group.length, 0);
  if (parts.groups.length === 0 && parts.keepOut.length === 0) {
    return { kind: "none" };
  }
  if (parts.groups.length <= 1 && parts.keepOut.length === 0) {
    return { kind: "simple", rules: parts.groups[0] ?? [] };
  }
  return {
    kind: "complex",
    groups: parts.groups.length,
    rules: total,
    keepOut: parts.keepOut.length,
  };
};

const text = (value: unknown): string =>
  typeof value === "string" ? value : value == null ? "" : String(value);

/** The rules the screen edits, from stored Filters. */
export const draftRulesOf = (
  filters: { rules: unknown } | undefined,
  newKey: () => number
): DraftRule[] => {
  const shape = shapeOf(filters);
  if (shape.kind !== "simple") return [];
  return shape.rules.map((stored) => {
    const fields: RuleFields = {
      propertyName: text(stored.propertyName),
      condition: text(stored.condition),
      propertyValue: text(stored.propertyValue),
      propertyValues: Array.isArray(stored.propertyValues)
        ? stored.propertyValues.filter(
            (item): item is string => typeof item === "string"
          )
        : [],
    };
    return { key: newKey(), ...fields };
  });
};

/** One rule as it is stored: a value only for a condition that takes one. */
const syncRuleOf = (rule: RuleFields) => {
  const base = { propertyName: rule.propertyName, condition: rule.condition };
  if (takesNoValue(rule.condition)) return base;
  if (takesValueList(rule.condition)) {
    return { ...base, propertyValues: rule.propertyValues };
  }
  return { ...base, propertyValue: rule.propertyValue };
};

/** Stored Filters from the rules on the screen; none when there are no rules. */
export const filtersOfDraft = (
  rules: DraftRule[]
): { rules: unknown } | undefined =>
  rules.length === 0
    ? undefined
    : {
        rules: {
          version: 1,
          include: {
            type: "group",
            operator: "or",
            conditions: [
              {
                type: "group",
                operator: "and",
                conditions: rules.map(syncRuleOf),
              },
            ],
          },
          exclude: { type: "group", operator: "or", conditions: [] },
        },
      };

/** A rule added on the screen */
export const addDraftRule = (
  rules: DraftRule[],
  newKey: () => number
): DraftRule[] => [...rules, newRule(newKey)];

export const changeDraftRule = (
  rules: DraftRule[],
  key: number,
  patch: Partial<RuleFields>
): DraftRule[] =>
  rules.map((rule) => (rule.key === key ? { ...rule, ...patch } : rule));

export const removeDraftRule = (rules: DraftRule[], key: number): DraftRule[] =>
  rules.filter((rule) => rule.key !== key);

/**
 * What is missing in the stored Filters before the server's reader is asked,
 * in words for the marketer: a rule with no field, or with no value where it
 * needs one. Undefined when nothing is missing.
 */
export const incompleteFilterProblem = (
  filters: { rules: unknown } | undefined
): string | undefined => {
  const parts = partsOf(filters);
  if (!parts) return undefined;
  const all = [...parts.groups.flat(), ...parts.keepOut];
  if (all.some((rule) => text(rule.propertyName) === "")) {
    // flow.opportunity.filters.field
    return "Choose a field for each Filter.";
  }
  const lacking = all.find((rule) => {
    const condition = text(rule.condition);
    if (condition === "" || takesNoValue(condition)) return false;
    if (takesValueList(condition)) {
      return !(
        Array.isArray(rule.propertyValues) && rule.propertyValues.length > 0
      );
    }
    return text(rule.propertyValue) === "";
  });
  // flow.opportunity.filters.value
  return lacking ? "Give each Filter a value to compare with." : undefined;
};

// ---------------------------------------------------------------------------
// The fields a Filter can read

export type FilterFields = {
  options: FieldOption[];
  context: FieldContext;
  /** The label of a field by its API name; the API name when the list does not have it */
  labelOf: (apiName: string) => string;
};

const infoOf = (field: SalesforceOpportunityFieldInfo): FieldInfo => ({
  name: field.name,
  label: field.label || field.name,
  type: typeof field.type === "string" ? field.type.toLowerCase() : "string",
  calculated: Boolean(field.calculated),
  ...(field.picklistValues && field.picklistValues.length > 0
    ? { picklistValues: field.picklistValues }
    : {}),
});

/**
 * The fields to pick from in the Filters, from the Opportunity fields route:
 * those a Filter can read (formula, roll-up and related fields too), by label.
 * A related field is shown with the record it belongs to ("Owner: Name").
 */
export const filterFieldsOf = (
  fields: SalesforceOpportunityFieldInfo[] | undefined
): FilterFields => {
  const usable = (fields ?? []).filter((field) => field.canFilter);
  const infos = usable.map(infoOf);
  const byName = new Map(infos.map((info) => [info.name.toLowerCase(), info]));
  const options = infos
    .map((info) => ({
      value: info.name,
      label: info.calculated ? `${info.label} (formula)` : info.label,
    }))
    .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
  return {
    options,
    context: {
      fieldOf: (_object, name) => byName.get(name.toLowerCase()),
      hasDescribe: () => usable.length > 0,
      refused: () => [],
    },
    labelOf: (apiName) => byName.get(apiName.toLowerCase())?.label ?? apiName,
  };
};

/** The fields a Field start can pick: those the route says can start a flow, without the Opportunity's name (its own start). */
export const startFieldOptionsOf = (
  fields: SalesforceOpportunityFieldInfo[] | undefined
): Array<{ value: string; label: string }> =>
  (fields ?? [])
    .filter((field) => field.canStart && field.name.toLowerCase() !== "name")
    .map((field) => ({ value: field.name, label: field.label || field.name }))
    .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
